import { FC, useEffect, useState } from 'react';
import { StanInput, StanModal } from '@/components';
import { autoAddHttps, isValidURL, isValidURLWithoutHttp } from '@/lib';

interface LinkButtonData {
    title: string;
    url: string;
    type: 'web_url';
}

interface LinkButtonModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: LinkButtonData) => void;
    onRemove: () => void;
    currentButton?: LinkButtonData | null;
}

export const LinkButtonModal: FC<LinkButtonModalProps> = ({ isOpen, onClose, onSave, onRemove, currentButton }) => {
    const [buttonTitle, setButtonTitle] = useState('');
    const [buttonUrl, setButtonUrl] = useState('');
    const [urlError, setUrlError] = useState(false);

    const isEdit = !!currentButton;

    // Reset form when modal opens/closes or currentButton changes
    useEffect(() => {
        if (isOpen) {
            if (currentButton) {
                setButtonTitle(currentButton.title || '');
                setButtonUrl(currentButton.url || '');
            } else {
                setButtonTitle('');
                setButtonUrl('');
            }
            setUrlError(false);
        }
    }, [isOpen, currentButton]);

    // Clear URL error when user types
    useEffect(() => {
        setUrlError(false);
    }, [buttonUrl]);

    const isValid = buttonTitle.trim().length > 0 && buttonUrl.trim().length > 0 && !urlError;

    const validateUrl = (url: string): boolean => {
        if (!url) return false;
        if (isValidURL(url) || isValidURLWithoutHttp(url)) {
            setUrlError(false);
            return true;
        }
        setUrlError(true);
        return false;
    };

    const handleSave = () => {
        if (!isValid) return;

        // Validate URL format
        if (!validateUrl(buttonUrl)) {
            return;
        }

        const buttonData: LinkButtonData = {
            title: buttonTitle,
            url: autoAddHttps(buttonUrl),
            type: 'web_url', // Required for Facebook API integration
        };

        onSave(buttonData);
        onClose();
    };

    const handleRemove = () => {
        onRemove();
        onClose();
    };

    return (
        <StanModal
            id='link-button-modal'
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Button' : 'Add a Link Button'}
            size='sm'
            primaryActionLabel={isEdit ? 'Save' : 'Add'}
            onPrimaryAction={handleSave}
            primaryActionDisabled={!isValid}
            secondaryActionLabel={isEdit ? 'Remove Button' : undefined}
            onSecondaryAction={isEdit ? handleRemove : undefined}
        >
            <div className='flex flex-col gap-4'>
                <div>
                    <div className='mb-2 flex items-center justify-between'>
                        <label htmlFor='button-title' className='text-label-sm-bold'>
                            Button Text
                        </label>
                        <span className='text-caption-2-regular text-gray'>{buttonTitle.length} / 30</span>
                    </div>
                    <StanInput
                        id='button-title'
                        name='link-button-modal-title-input'
                        value={buttonTitle}
                        onChange={e => setButtonTitle(e.target.value)}
                        placeholder='Button text'
                        maxLength={30}
                    />
                </div>

                <div>
                    <label htmlFor='button-url' className='text-label-sm-bold mb-2 block'>
                        Button URL
                    </label>
                    <StanInput
                        id='button-url'
                        name='link-button-modal-url-input'
                        value={buttonUrl}
                        onChange={e => setButtonUrl(e.target.value)}
                        placeholder='https://...'
                        error={urlError}
                    />
                    {urlError && <p className='text-caption-2-bold text-negative mt-1'>Please enter a valid link</p>}
                </div>

                <p className='text-caption-2-regular text-neutral-fg-secondary -mt-2 italic'>
                    Adding a link button disables plain text links
                </p>
            </div>
        </StanModal>
    );
};
